import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { Images } from 'lucide-react';
import {
  MEDIA_KIND_STATUS_CLASS,
  MEDIA_KIND_STATUS_LABELS,
  MEDIA_KIND_STATUS_VARIANT,
} from 'modules/championship/ui/components/form-parts/media/media-kind-status';
import {
  MEDIA_ASSIGNED_CARD_CLASS,
  MEDIA_ASSIGNED_ICON_CLASS,
  MEDIA_KIND_ACTION_CLASS,
  MEDIA_ROW_LIST_CLASS,
  MEDIA_SECTION_CLASS,
  MEDIA_TEMPLATES_COLUMN_CLASS,
  MEDIA_TEMPLATES_COPY,
} from 'modules/championship/ui/components/form-parts/media/media-tab-layout';
import MediaTabStateCard from 'modules/championship/ui/components/form-parts/media/media-tab-state-card';
import React from 'react';
import { UDEntityRow } from 'ud-ui/components/paged-list';
import { UDSkeletonButton, UDSkeletonInline, UDSkeletonLabel, UDSkeletonText } from 'ud-ui/components/skeleton';
import UDText from 'ud-ui/components/text';

/**
 * Сколько строк рисовать, пока ответа нет: столько типов медиа у набора сейчас и не меньше
 * наборов у живой организации. Ошибка в числе стоит одного сдвига низа списка, а не всего экрана.
 */
export const MEDIA_TEMPLATES_SKELETON_ROW_COUNT = 3;

/**
 * - `assigned` — «набор назначен»: настроек турнира ещё нет. На «Шаблоны» без набора приводит
 *   редирект уже после загрузки, а с набором сюда приходят за генерацией — поэтому по умолчанию он.
 * - `picker` — «набор не выбран»: настройки пришли без набора, а наборы организации ещё летят.
 */
export type ChampionshipMediaTemplatesSkeletonVariant = 'assigned' | 'picker';

interface ChampionshipMediaTemplatesSkeletonProps {
  variant: ChampionshipMediaTemplatesSkeletonVariant;
}

/**
 * Заглушка подвкладки «Шаблоны» на время загрузки (IMGM-1491).
 *
 * Повторяет раскладку `ChampionshipMediaTemplates` карточка в карточку: те же классы из
 * `media-tab-layout.ts`, те же `UDEntityRow` и `MediaTabStateCard`. Статичный текст — настоящий,
 * данные — плашки кита `ud-ui/components/skeleton` (`.claude/docs/skeleton-kit.md`). Обёртка —
 * один элемент `w-full` (`.claude/docs/async-loading-states.md`, «Скелетон в слоте»).
 */
const ChampionshipMediaTemplatesSkeleton = ({ variant }: ChampionshipMediaTemplatesSkeletonProps) => (
  <div className={MEDIA_TEMPLATES_COLUMN_CLASS} data-testid="championship-media-skeleton" data-variant={variant} aria-busy="true">
    {variant === 'assigned' ? <AssignedSkeleton /> : <PickerSkeleton />}
  </div>
);

const AssignedSkeleton = () => {
  const { t } = useLingui();

  return (
    <>
      <section className={MEDIA_ASSIGNED_CARD_CLASS}>
        <div className={classNames(MEDIA_ASSIGNED_ICON_CLASS, 'skeleton-block')} aria-hidden />
        <div className="min-w-0 flex-1">
          <UDText type="footnote" className="text-inkSecondary">
            {t(MEDIA_TEMPLATES_COPY.assignedLabel)}
          </UDText>
          <UDSkeletonText type="subhead" className="w-48" />
          <UDSkeletonText type="footnote" className="w-20" />
        </div>
        <div className="flex shrink-0 gap-3">
          <UDSkeletonButton label={t(MEDIA_TEMPLATES_COPY.change)} />
          <UDSkeletonButton label={t(MEDIA_TEMPLATES_COPY.unassign)} />
        </div>
      </section>

      <section className={MEDIA_SECTION_CLASS}>
        <div>
          <UDText type="title-3">{t(MEDIA_TEMPLATES_COPY.kindsTitle)}</UDText>
          <UDText type="subhead" className="text-inkSecondary">
            {t(MEDIA_TEMPLATES_COPY.kindsSubtitle)}
          </UDText>
        </div>
        {/* Самая частая строка — «готов к генерации» с кнопкой: у набора из общих шаблонов готовы почти все типы. */}
        <SkeletonRows
          action={
            <>
              <UDSkeletonLabel
                label={t(MEDIA_KIND_STATUS_LABELS.ready)}
                className={MEDIA_KIND_STATUS_CLASS}
                variant={MEDIA_KIND_STATUS_VARIANT}
              />
              <UDSkeletonButton label={t(MEDIA_TEMPLATES_COPY.generate)} className={MEDIA_KIND_ACTION_CLASS} />
            </>
          }
        />
      </section>
    </>
  );
};

const PickerSkeleton = () => {
  const { t } = useLingui();

  return (
    <div className={MEDIA_SECTION_CLASS}>
      <MediaTabStateCard
        icon={<Images size={26} />}
        title={t(MEDIA_TEMPLATES_COPY.pickTitle)}
        subtitle={t(MEDIA_TEMPLATES_COPY.pickSubtitle)}
      />
      <SkeletonRows action={<UDSkeletonButton label={t(MEDIA_TEMPLATES_COPY.assign)} />} />
    </div>
  );
};

/** Строки — настоящие `UDEntityRow`: высоту, отступы и радиус даёт сам кит. */
const SkeletonRows = ({ action }: { action: React.ReactNode }) => (
  <div className={MEDIA_ROW_LIST_CLASS} role="list" aria-hidden>
    {Array.from({ length: MEDIA_TEMPLATES_SKELETON_ROW_COUNT }, (_, index) => (
      <UDEntityRow
        key={index}
        data-testid="championship-media-skeleton-row"
        title={<UDSkeletonInline className="w-44" />}
        subtitle={<UDSkeletonInline className="w-56" />}
        actions={action}
      />
    ))}
  </div>
);

export default ChampionshipMediaTemplatesSkeleton;
